export type MikroSuggestionEvent = {
  id: string;
  type: "DEMAND" | "INBOUND";
  eventAt: string | null;
  referenceNo: string;
  referenceCount: number;
  label: string;
  quantity: number;
};

export type MikroPurchaseSuggestion = {
  id: string;
  materialCode: string;
  materialName: string;
  warehouseCode: string;
  warehouseName: string;
  unit: string;
  onHandQuantity: number;
  inboundQuantity: number;
  minimumStockQuantity: number;
  leadTimeDays: number;
  totalRequiredQuantity: number;
  workOrderCount: number;
  overdueRequiredQuantity: number;
  overdueWorkOrderCount: number;
  firstRequiredAt: string | null;
  lastRequiredAt: string | null;
  projectedStockQuantity: number;
  productionNeedQuantity: number;
  minimumStockNeedQuantity: number;
  events: MikroSuggestionEvent[];
};

export type MikroPurchaseSuggestionResult = {
  items: MikroPurchaseSuggestion[];
  source: "MICRO_ERP";
  refreshedAt: string;
  truncated: boolean;
};

export type SuggestedRequestDraft = {
  title: string;
  description: string;
  category: "Hammadde" | "Ambalaj";
  quantity: string;
  unit: string;
  priority: "NORMAL" | "HIGH" | "CRITICAL";
  due: string;
};

export type TimelineRow = {
  id: string;
  eventAt: string | null;
  label: string;
  change: number;
  balance: number;
  ignored: boolean;
  shortage: boolean;
};

const quantityFormatter = new Intl.NumberFormat("tr-TR", {
  maximumFractionDigits: 3,
});

export function dateKey(value: string | null) {
  return value?.match(/^(\d{4})-(\d{2})-(\d{2})/)?.[0] ?? null;
}

export function todayKey() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function formatDate(value: string | null) {
  const key = dateKey(value);
  if (!key) return "Tarih bilinmiyor";
  const [year, month, day] = key.split("-").map(Number);
  return new Intl.DateTimeFormat("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
}

export function formatDateTime(value: string | null) {
  if (!value) return "Bilinmiyor";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("tr-TR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

export function formatQuantity(value: number, unit: string) {
  return `${quantityFormatter.format(value)} ${unit || "birim"}`;
}

function normalizeRequestQuantity(value: number, unit: string) {
  const discreteUnits = new Set(["ADET", "KOLİ", "PAKET", "RULO", "PALET", "SET"]);
  return discreteUnits.has(unit.toLocaleUpperCase("tr-TR"))
    ? Math.ceil(value)
    : Math.ceil(value * 1000) / 1000;
}

function subtractDays(value: string | null, days: number) {
  const key = dateKey(value);
  if (!key) return null;
  const [year, month, day] = key.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() - days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function addDays(value: string, days: number) {
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function requestCategory(materialCode: string): "Hammadde" | "Ambalaj" {
  return materialCode.startsWith("4") ? "Ambalaj" : "Hammadde";
}

function groupEvents(events: MikroSuggestionEvent[]) {
  const groups = new Map<
    string,
    {
      id: string;
      type: MikroSuggestionEvent["type"];
      eventAt: string | null;
      quantity: number;
      references: string[];
      referenceCount: number;
    }
  >();
  for (const event of events) {
    const key = `${dateKey(event.eventAt) ?? "unknown"}:${event.type}`;
    const group = groups.get(key) ?? {
      id: key,
      type: event.type,
      eventAt: event.eventAt,
      quantity: 0,
      references: [],
      referenceCount: 0,
    };
    group.quantity += event.quantity;
    group.referenceCount += event.referenceCount;
    if (event.referenceNo && !group.references.includes(event.referenceNo)) {
      group.references.push(event.referenceNo);
    }
    groups.set(key, group);
  }
  return [...groups.values()].sort((left, right) => {
    const dateOrder = (dateKey(left.eventAt) ?? "9999").localeCompare(
      dateKey(right.eventAt) ?? "9999",
    );
    if (dateOrder !== 0) return dateOrder;
    return left.type === "INBOUND" ? -1 : 1;
  });
}


export function calculateSuggestion(suggestion: MikroPurchaseSuggestion | null, includeOverdue: boolean, includeMinimumStock: boolean, today = todayKey()) {
  if (!suggestion) {
    return {
      rows: [],
      balance: 0,
      requestQuantity: 0,
      firstShortageAt: null,
      latestOrderAt: null,
    };
  }
  let balance = suggestion.onHandQuantity;
  let firstShortageAt: string | null = balance < 0 ? today : null;
  const rows: TimelineRow[] = [];
  for (const group of groupEvents(suggestion.events)) {
    const overdueDemand =
      group.type === "DEMAND" &&
      Boolean(dateKey(group.eventAt)) &&
      (dateKey(group.eventAt) ?? "9999") < today;
    const ignored = overdueDemand && !includeOverdue;
    const change = group.type === "INBOUND" ? group.quantity : -group.quantity;
    if (!ignored) balance += change;
    if (!firstShortageAt && balance < 0) firstShortageAt = group.eventAt;
    const referencePreview = group.references.slice(0, 2).join(", ");
    const additionalCount = Math.max(0, group.referenceCount - group.references.length);
    const label = group.type === "INBOUND"
      ? `${group.referenceCount} açık satın alma siparişi${referencePreview ? ` · ${referencePreview}` : ""}`
      : `${overdueDemand ? "Gecikmiş · " : ""}${group.referenceCount} iş emri${referencePreview ? ` · ${referencePreview}` : ""}${additionalCount ? ` +${additionalCount}` : ""}`;
    rows.push({
      id: group.id,
      eventAt: group.eventAt,
      label,
      change,
      balance,
      ignored,
      shortage: balance < 0,
    });
  }
  const productionNeed = Math.max(0, -balance);
  const rawRequestQuantity = includeMinimumStock
    ? Math.max(0, suggestion.minimumStockQuantity - balance)
    : productionNeed;
  const requestQuantity = normalizeRequestQuantity(
    rawRequestQuantity,
    suggestion.unit,
  );
  return {
    rows,
    balance,
    requestQuantity,
    firstShortageAt,
    latestOrderAt: subtractDays(firstShortageAt, suggestion.leadTimeDays),
  };

}

export function suggestionDraft(suggestion: MikroPurchaseSuggestion, calculation: ReturnType<typeof calculateSuggestion>, includeOverdue: boolean, includeMinimumStock: boolean, today = todayKey()): SuggestedRequestDraft {
  const shortageKey = dateKey(calculation.firstShortageAt);
  const due = shortageKey && shortageKey >= today ? shortageKey : today;
  const latestOrderKey = dateKey(calculation.latestOrderAt);
  const priority: SuggestedRequestDraft["priority"] =
    !latestOrderKey || latestOrderKey <= today
      ? "CRITICAL"
      : latestOrderKey <= addDays(today, 7)
        ? "HIGH"
        : "NORMAL";
  return {
    title: suggestion.materialName || suggestion.materialCode,
    description: [
      `Mikro ${suggestion.materialCode} kodlu malzeme için ${suggestion.workOrderCount} açık iş emrinin hesaplanan satın alma ihtiyacı.`,
      `Depo: ${suggestion.warehouseCode}${suggestion.warehouseName ? ` · ${suggestion.warehouseName}` : ""}.`,
      `Mevcut stok ${formatQuantity(suggestion.onHandQuantity, suggestion.unit)}, zamanında açık giriş ${formatQuantity(suggestion.inboundQuantity, suggestion.unit)}, toplam planlanan tüketim ${formatQuantity(suggestion.totalRequiredQuantity, suggestion.unit)}.`,
      suggestion.overdueWorkOrderCount > 0
        ? `${suggestion.overdueWorkOrderCount} gecikmiş iş emri ${includeOverdue ? "hesaba dahil edildi" : "hesap dışında bırakıldı"}.`
        : "",
      includeMinimumStock
        ? `Mikro minimum stok hedefi ${formatQuantity(suggestion.minimumStockQuantity, suggestion.unit)} talep miktarına dahil edildi.`
        : "Minimum stok hedefi talep miktarına dahil edilmedi.",
    ].filter(Boolean).join(" "),
    category: requestCategory(suggestion.materialCode),
    quantity: String(calculation.requestQuantity),
    unit: suggestion.unit.toLocaleLowerCase("tr-TR") || "adet",
    priority,
    due,
  };
}
